import type { MRT_TableInstance } from '@talentmesh/table';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@talentmesh/ui/components/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@talentmesh/ui/components/select';
import React, { useEffect, useMemo } from 'react';
import Page, { Pagination as PaginationModel } from '@/Models/Page';
import UIStrings from '@/Utils/UIStrings';

interface AiSearchListNavProps {
    pagination: PaginationModel;
    setPagination: React.Dispatch<React.SetStateAction<PaginationModel>>;
    data?: Page<any>;
    isLoading: boolean;
    table: MRT_TableInstance<any>;
    paginationSizeOptions: number[];
}

const AiSearchListNav = ({
    data,
    isLoading,
    pagination,
    table,
    setPagination,
    paginationSizeOptions,
}: AiSearchListNavProps) => {
    const currentPage = useMemo(
        () => Math.floor(pagination.offset / pagination.limit) + 1,
        [pagination.offset, pagination.limit],
    );
    const totalPages = useMemo(
        () => Math.ceil(pagination.totalCount / pagination.limit),
        [pagination.totalCount, pagination.limit],
    );
    const pageNumbers = useMemo(() => {
        const pages: number[] = [];

        for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
        }

        return pages;
    }, [totalPages]);

    const handlePageChange = (page: number) => {
        table.refs.tableContainerRef.current?.scrollTo(0, 0);

        setPagination((prev) => ({
            ...prev,
            offset: (page - 1) * prev.limit,
        }));

        table.resetRowSelection();
    };

    useEffect(() => {
        if (!isLoading && data?.pagination) {
            setPagination(data.pagination);
        }
    }, [data?.pagination, isLoading, setPagination]);

    return (
        <div className="my-5 flex flex-wrap items-center justify-center gap-5" data-cy="tableNav">
            <Pagination className="mx-0 w-auto justify-start">
                <PaginationContent>
                    <PaginationItem>
                        <PaginationPrevious
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                        />
                    </PaginationItem>

                    {pageNumbers.map((page) => (
                        <PaginationItem key={page}>
                            <PaginationLink
                                isActive={currentPage === page}
                                onClick={() => handlePageChange(page)}
                                className="h-8 w-8 rounded-lg p-0"
                            >
                                {page}
                            </PaginationLink>
                        </PaginationItem>
                    ))}

                    <PaginationItem>
                        <PaginationNext
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>

            <span className="text-xs">
                {UIStrings.PaginationSizeInfo(
                    pagination.totalCount > 0 ? pagination.offset + 1 : 0,
                    (data?.data.length || 0) + pagination.offset,
                    pagination.totalCount,
                )}
            </span>

            <div className="flex items-center gap-2">
                <Select
                    value={String(pagination.limit)}
                    onValueChange={(value) => {
                        const limit = Number(value);

                        setPagination((prev) => ({ ...prev, offset: 0, limit }));
                    }}
                >
                    <SelectTrigger
                        id="pageSizeButton"
                        aria-label="Page size"
                        className="border-input h-9 w-fit justify-between bg-transparent shadow-none"
                    >
                        <SelectValue />
                    </SelectTrigger>

                    <SelectContent align="end">
                        {paginationSizeOptions.map((sizeOption) => (
                            <SelectItem key={sizeOption} value={String(sizeOption)}>
                                {sizeOption}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="text-xs">{UIStrings.PerPage}</span>
            </div>
        </div>
    );
};

export default AiSearchListNav;
